import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as i18n from 'i18next';
import { State } from 'reducers';
import { Device, Building, FloorOverride, DeviceClass, DeviceFamily } from 'shared/models';
import { getBuildingDeviceGroupNames } from 'selectors/buildingsSelectors';
import { ErrorMessage } from 'ui/shared/messages';
import { LegacySelectField, CheckField, TextField } from 'ui/shared/controls';
import { Loading } from 'ui/shared/loading';
import { LegacyFloorOverrideTable } from './legacyFloorOverrideTable/LegacyFloorOverrideTable';
import { Permission } from 'shared/permissions';
import { useSession } from 'hooks';
import { useDeviceTypes } from 'hooks/useDeviceTypes/useDeviceTypes';
import { ChangeEvent, FormEvent, useCallback, useState } from 'react';
import { merge } from 'lodash';

require('./LegacyDeviceForm.css');

const nullGroupValue = 'none';

const makeGroupNameOptions = (t: i18n.TFunction, groupNames: string[]) =>
    [
        // Note: couldn't use null or '' for the no-group value... Semantic UI mishandled it
        { value: nullGroupValue, text: t('DEVICES.NO_GROUP') },
        ...groupNames.sort().map(name => ({ value: name, text: name })),
    ].map(item =>
        <option
            key={ `group-${item.text}` }
            value={ item.value }
        >
            { item.text }
        </option>
    );

export type Props = {
    busy: boolean;
    device: Device | null;
    building: Building;
    pin: string;
    pinPending: boolean;
    errorMessage: string;
    onSubmit(device: Device): void;
    onClickCancel(): void;
    onClickDelete(): void;
    onCreatePin(): void;
};

export const LegacyDeviceForm = ({ busy, device, building, pin, pinPending, errorMessage, onSubmit, onClickCancel, onClickDelete, onCreatePin }: Props) => {
    const { t } = useTranslation();
    const { hasPermission } = useSession();
    const { deviceTypes, deviceTypeOrder } = useDeviceTypes();

    const isNew = !device;

    const buildingId = building.id;
    const groupNames = useSelector((state: State) => buildingId ? getBuildingDeviceGroupNames(state, { buildingId }) : []);
    const isReady = !!(building && deviceTypes);

    const firstDeviceTypeId = deviceTypeOrder && deviceTypeOrder[0];
    const initialValues = device || { buildingId, deviceTypeId: firstDeviceTypeId, properties: { pi: true } } as Device;

    const [ deviceDetails, setDeviceDetails ] = useState(initialValues);

    const deviceType = deviceTypes[deviceDetails.deviceTypeId];
    const deviceClass = deviceType ? deviceType.deviceClass : DeviceClass.Unknown;

    const isDisplay = deviceClass === DeviceClass.Display;
    const supportsPin =
        isDisplay && [
            DeviceFamily.MatisseR4,
            DeviceFamily.MatisseR5,
            DeviceFamily.TTG,
            DeviceFamily.MatisseFlexR2,
        ].includes(deviceType.deviceFamily!);

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSubmit({
            ...deviceDetails,
            // HACK: workaround Semantic UI not supporting a null option value
            groupName: deviceDetails.groupName === nullGroupValue ? null : deviceDetails.groupName,
            buildingId,
        });
        return false;
    };

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        setDeviceDetails(d => ({
            ...d,
            [name]: value,
        }));
    }, [ setDeviceDetails ]);

    const handleChangePropertiesText = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.currentTarget;
        setDeviceDetails(d => merge({}, d, { properties: { [name]: value } }));
    }, [ setDeviceDetails ]);

    const handleChangePropertiesBoolean = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.currentTarget;
        setDeviceDetails(d => merge({}, d, { properties: { [name]: checked } }));
    }, [ setDeviceDetails ]);

    const handleChangeFloorOverride = useCallback((index: number, fnUpdate: (mapping: Readonly<FloorOverride>) => FloorOverride) => {
        setDeviceDetails(d => {
            const { properties = {} } = d;
            const { floorOverride = [] } = properties;
            return {
                ...d,
                properties: {
                    ...properties,
                    floorOverride: floorOverride.map((m, i) =>
                        i === index
                            ? fnUpdate(m)
                            : m
                    ),
                },
            };
        });
    }, [ setDeviceDetails ]);

    const handleAddFloorOverrideRow = useCallback(() => {
        setDeviceDetails(d => {
            const { properties = {} } = d;
            const { floorOverride = [] } = properties;
            return {
                ...d,
                properties: {
                    ...properties,
                    floorOverride: [
                        ...floorOverride,
                        {
                            from: '',
                            to: '',
                        },
                    ],
                },
            };
        });
    }, [ setDeviceDetails ]);

    const handleRemoveFloorOverrideRow = useCallback((index: number) => {
        setDeviceDetails(d => {
            const { properties = {} } = d;
            const { floorOverride = [] } = properties;
            return {
                ...d,
                properties: {
                    ...properties,
                    floorOverride: floorOverride.filter((m, i) => i !== index),
                },
            };
        });
    }, [ setDeviceDetails ]);

    // Don't render anything if either the building or the device types haven't loaded yet
    if (!isReady || !hasPermission(buildingId, Permission.ViewDevices)) {
        return null;
    }
    return (
        <div>
            <form
                id="legacy-device-form"
                onSubmit={ handleSubmit }
                className={ `ui form${errorMessage ? ' error' : ''}${busy ? ' loading' : ''}` }
            >
                <input
                    id="edit-device-building-id"
                    name="buildingId"
                    type="hidden"
                    value={ device ? device.buildingId : '' }
                />

                <div className="ui grid">
                    <div className="eight wide column">
                        { hasPermission(buildingId, Permission.EditDevice) &&
                            <LegacySelectField
                                id="edit-device-type"
                                required={ true }
                                labelKey="DEVICES.EDIT_DIALOG.FIELD.DEVICE_TYPE"
                                input={ {
                                    /* KILL: remove this input attribute once redux-form is gone */
                                    name: "deviceTypeId",
                                    value: deviceDetails.deviceTypeId,
                                    onChange: handleChange,
                                } }
                            >
                                { deviceTypeOrder
                                    .filter(id => device ? deviceTypes[id].deviceClass === device.deviceClass : DeviceClass.Display)
                                    .map(id =>
                                        <option
                                            key={ id }
                                            value={ id }
                                        >
                                            { deviceTypes[id].displayName }
                                        </option>
                                    ) }
                            </LegacySelectField>
                        }
                        { !hasPermission(buildingId, Permission.EditDevice) &&
                            <TextField
                                readOnly={ true }
                                id="edit-device-type"
                                labelKey="DEVICES.EDIT_DIALOG.FIELD.DEVICE_TYPE"
                                value={ deviceTypes[deviceDetails.deviceTypeId] ? deviceTypes[deviceDetails.deviceTypeId].displayName : '' }
                            />
                        }
                    </div>
                    <div className="eight wide column">
                        <LegacySelectField
                            id="edit-device-group"
                            labelKey="DEVICES.EDIT_DIALOG.FIELD.GROUP"
                            allowAdditions={ true }
                            input={ {
                                name: 'groupName',
                                className: "ui fluid search dropdown",
                                maxLength:  32,
                                value: deviceDetails.groupName || nullGroupValue,
                                onChange: handleChange,
                            } }
                        >
                            { makeGroupNameOptions(t, groupNames) }
                        </LegacySelectField>
                    </div>
                </div>

                <div className="ui grid">
                    <div className="eight wide column">
                        <TextField
                            id="edit-device-display-name"
                            name="displayName"
                            labelKey="DEVICES.EDIT_DIALOG.FIELD.DISPLAY_NAME"
                            maxLength={ 32 }
                            autoComplete="off"
                            value={ deviceDetails.displayName || '' }
                            onChange={ handleChange }
                        />
                    </div>
                </div>

                { (isNew || hasPermission(buildingId, Permission.EditDevice)) && isDisplay &&
                    <div className="ui grid">
                        <div className="sixteen wide column">
                            <CheckField
                                id="edit-device-type-pi"
                                labelKey="DEVICES.EDIT_DIALOG.FIELD.PI"
                                input={ {
                                    name: "pi",
                                    checked: deviceDetails.properties.pi,
                                    onChange: handleChangePropertiesBoolean,
                                } }
                            />
                        </div>
                    </div>
                }
                { deviceDetails.properties.pi && hasPermission(buildingId, Permission.EditDevice) && isDisplay &&
                    <div className="ui grid">
                        <div className="eight wide column">
                            <TextField
                                id="edit-device-elevator-id"
                                name="elevatorId"
                                labelKey="DEVICES.EDIT_DIALOG.FIELD.ELEVATOR_ID"
                                autoComplete="off"
                                value={ deviceDetails.properties.elevatorId || '' }
                                onChange={ handleChangePropertiesText }
                            />
                            <TextField
                                id="edit-device-max-persons"
                                name="maxPersons"
                                labelKey="DEVICES.EDIT_DIALOG.FIELD.MAX_PERSONS"
                                maxLength={ 128 }
                                autoComplete="off"
                                value={ deviceDetails.properties.maxPersons || '' }
                                onChange={ handleChangePropertiesText }
                            />
                            <TextField
                                id="edit-device-max-weight"
                                name="maxWeight"
                                labelKey="DEVICES.EDIT_DIALOG.FIELD.MAX_WEIGHT"
                                maxLength={ 128 }
                                autoComplete="off"
                                value={ deviceDetails.properties.maxWeight || '' }
                                onChange={ handleChangePropertiesText }
                            />

                            <div className="ui grid">
                                <div className="sixteen wide column">
                                </div>
                            </div>
                        </div>
                        <div className="eight wide column">
                            <div className="ui grid">
                                <LegacyFloorOverrideTable
                                    values={ deviceDetails.properties.floorOverride || [] }
                                    onChange={ handleChangeFloorOverride }
                                    onAddRow={ handleAddFloorOverrideRow }
                                    onRemoveRow={ handleRemoveFloorOverrideRow }
                                />
                            </div>
                        </div>
                    </div>
                }

                <div className="ui grid" style={ { marginTop: '30px' } }>
                    <div className="eight wide column">
                        <button
                            className="ui primary submit button"
                            type="submit"
                            disabled={ busy }
                        >
                            { t(isNew ? 'COMMAND.CREATE_DEVICE' : 'COMMAND.SAVE') }
                        </button>
                        <button
                            className="ui button"
                            type="button"
                            disabled={ busy }
                            onClick={ onClickCancel }
                        >
                            { t('COMMAND.CANCEL') }
                        </button>
                    </div>
                    <div className="five wide column">
                        { !isNew && pinPending &&
                            <Loading/>
                        }
                        { !isNew && pin &&
                            <span className="_pin">
                                { pin }
                            </span>
                        }
                        { !isNew && supportsPin && !pinPending && !pin && hasPermission(buildingId, Permission.EditDevice) &&
                            <button
                                type="button"
                                onClick={ () => onCreatePin() }
                                className="ui button"
                            >
                                { t('COMMAND.CREATE_PIN') }
                            </button>
                        }
                    </div>
                    <div className="three wide column">
                        { hasPermission(buildingId, Permission.DeleteDevice) && device &&
                            <button
                                className="ui red basic button _delete-button"
                                type="button"
                                disabled={ busy }
                                onClick={ onClickDelete }
                            >
                                { t('COMMAND.DELETE') }
                            </button>
                        }
                    </div>
                </div>
                <ErrorMessage message={ errorMessage }/>
            </form>
        </div>
    );
};